import React, { useState, useRef, useCallback, useEffect } from 'react';
import Stack from '@/components/Stack';
import Button from '@/components/Button';
import { Body, Small } from '@/components/Typography';
import { SignatureField } from '@/types/documents';

const PDFJS_CDN = 'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.min.mjs';
const PDFJS_WORKER_CDN =
  'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs';

// Default sizes in PDF points
const DEFAULT_SIGNATURE_WIDTH = 200;
const DEFAULT_SIGNATURE_HEIGHT = 60;
const DEFAULT_DATE_WIDTH = 150;
const DEFAULT_DATE_HEIGHT = 20;

// Minimum sizes in screen pixels
const MIN_SIGNATURE_SCREEN_W = 100;
const MIN_SIGNATURE_SCREEN_H = 30;
const MIN_DATE_SCREEN_W = 50;
const MIN_DATE_SCREEN_H = 15;

const HANDLE_SIZE = 8;

// Lazy-loaded pdfjs library
let pdfjsLib: any = null;

async function loadPdfJs(): Promise<any> {
  if (pdfjsLib) return pdfjsLib;
  pdfjsLib = await import(/* webpackIgnore: true */ PDFJS_CDN);
  pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
  return pdfjsLib;
}

interface InternalField extends SignatureField {
  _id: number;
}

interface PdfSignaturePlacerProps {
  pdfUrl: string;
  initialFields?: SignatureField[];
  onFieldsChange: (fields: SignatureField[]) => void;
}

type ResizeHandle = 'tl' | 'tr' | 'bl' | 'br';

let nextFieldId = 1;

function stripInternalId(field: InternalField): SignatureField {
  const { _id, ...rest } = field;
  return rest;
}

const PdfSignaturePlacer: React.FC<PdfSignaturePlacerProps> = ({
  pdfUrl,
  initialFields,
  onFieldsChange,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageWidth, setPageWidth] = useState<number>(612);
  const [pageHeight, setPageHeight] = useState<number>(792);
  const [scale, setScale] = useState<number>(1);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);

  const [fields, setFields] = useState<InternalField[]>(() =>
    (initialFields || []).map((f) => ({ ...f, _id: nextFieldId++ })),
  );
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizing, setResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null);
  const [resizeAnchor, setResizeAnchor] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfDocRef = useRef<any>(null);

  // Notify parent of field changes
  const emitFields = useCallback(
    (updatedFields: InternalField[]) => {
      onFieldsChange(updatedFields.map(stripInternalId));
    },
    [onFieldsChange],
  );

  // PDF to screen coordinate conversion
  const pdfToScreen = useCallback(
    (px: number, py: number, pw: number, ph: number) => ({
      screenX: px * scale,
      screenY: (pageHeight - py - ph) * scale,
      screenW: pw * scale,
      screenH: ph * scale,
    }),
    [scale, pageHeight],
  );

  // Screen to PDF coordinate conversion
  const screenToPdf = useCallback(
    (sx: number, sy: number, sw: number, sh: number) => ({
      pdfX: sx / scale,
      pdfY: pageHeight - (sy + sh) / scale,
      pdfW: sw / scale,
      pdfH: sh / scale,
    }),
    [scale, pageHeight],
  );

  // Load PDF document
  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setPdfLoading(true);
      setPdfError(null);
      try {
        const lib = await loadPdfJs();
        const doc = await lib.getDocument(pdfUrl).promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
      } catch (err: any) {
        if (!cancelled) {
          setPdfError(`Failed to load PDF: ${err.message || err}`);
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  // Render current page to canvas
  useEffect(() => {
    const doc = pdfDocRef.current;
    if (!doc || currentPage < 1 || currentPage > numPages) return;

    let cancelled = false;

    async function renderPage() {
      try {
        const page = await doc.getPage(currentPage);
        if (cancelled) return;

        const viewport = page.getViewport({ scale: 1 });
        setPageWidth(viewport.width);
        setPageHeight(viewport.height);

        let newScale = 1;
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth - 32;
          newScale = Math.min(containerWidth / viewport.width, 1.5);
        }
        setScale(newScale);

        const scaledViewport = page.getViewport({ scale: newScale });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        await page.render({ canvasContext: ctx, viewport: scaledViewport })
          .promise;
      } catch (err: any) {
        if (!cancelled) {
          setPdfError(`Failed to render page: ${err.message || err}`);
        }
      }
    }

    renderPage();
    return () => {
      cancelled = true;
    };
  }, [numPages, currentPage]);

  const fieldsOnCurrentPage = fields.filter((f) => f.page === currentPage);

  const addField = useCallback(
    (fieldType: 'signature' | 'date') => {
      const w =
        fieldType === 'signature'
          ? DEFAULT_SIGNATURE_WIDTH
          : DEFAULT_DATE_WIDTH;
      const h =
        fieldType === 'signature'
          ? DEFAULT_SIGNATURE_HEIGHT
          : DEFAULT_DATE_HEIGHT;
      const x = (pageWidth - w) / 2;
      const y = (pageHeight - h) / 2;
      const maxSortOrder =
        fields.length > 0 ? Math.max(...fields.map((f) => f.sort_order)) : 0;

      const newField: InternalField = {
        _id: nextFieldId++,
        field_type: fieldType,
        page: currentPage,
        x,
        y,
        width: w,
        height: h,
        sort_order: maxSortOrder + 1,
      };

      const updated = [...fields, newField];
      setFields(updated);
      setSelectedFieldId(newField._id);
      emitFields(updated);
    },
    [fields, currentPage, pageWidth, pageHeight, emitFields],
  );

  const deleteField = useCallback(
    (fieldId: number) => {
      const updated = fields.filter((f) => f._id !== fieldId);
      setFields(updated);
      if (selectedFieldId === fieldId) {
        setSelectedFieldId(null);
      }
      emitFields(updated);
    },
    [fields, selectedFieldId, emitFields],
  );

  const updateField = useCallback(
    (fieldId: number, updates: Partial<InternalField>) => {
      const updated = fields.map((f) =>
        f._id === fieldId ? { ...f, ...updates } : f,
      );
      setFields(updated);
      emitFields(updated);
    },
    [fields, emitFields],
  );

  const getFieldAtScreenPos = useCallback(
    (mouseX: number, mouseY: number): InternalField | null => {
      // Check in reverse order so topmost fields are selected first
      for (let i = fieldsOnCurrentPage.length - 1; i >= 0; i--) {
        const f = fieldsOnCurrentPage[i];
        const { screenX, screenY, screenW, screenH } = pdfToScreen(
          f.x,
          f.y,
          f.width,
          f.height,
        );
        if (
          mouseX >= screenX &&
          mouseX <= screenX + screenW &&
          mouseY >= screenY &&
          mouseY <= screenY + screenH
        ) {
          return f;
        }
      }
      return null;
    },
    [fieldsOnCurrentPage, pdfToScreen],
  );

  const getResizeHandleAtPos = useCallback(
    (
      mouseX: number,
      mouseY: number,
      field: InternalField,
    ): ResizeHandle | null => {
      const { screenX, screenY, screenW, screenH } = pdfToScreen(
        field.x,
        field.y,
        field.width,
        field.height,
      );
      const handles: { handle: ResizeHandle; cx: number; cy: number }[] = [
        { handle: 'tl', cx: screenX, cy: screenY },
        { handle: 'tr', cx: screenX + screenW, cy: screenY },
        { handle: 'bl', cx: screenX, cy: screenY + screenH },
        { handle: 'br', cx: screenX + screenW, cy: screenY + screenH },
      ];
      for (const { handle, cx, cy } of handles) {
        if (
          mouseX >= cx - HANDLE_SIZE &&
          mouseX <= cx + HANDLE_SIZE &&
          mouseY >= cy - HANDLE_SIZE &&
          mouseY <= cy + HANDLE_SIZE
        ) {
          return handle;
        }
      }
      return null;
    },
    [pdfToScreen],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const rect = pageContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Check if clicking a resize handle on selected field
      if (selectedFieldId !== null) {
        const selectedField = fields.find((f) => f._id === selectedFieldId);
        if (selectedField && selectedField.page === currentPage) {
          const handle = getResizeHandleAtPos(mouseX, mouseY, selectedField);
          if (handle) {
            const { screenX, screenY, screenW, screenH } = pdfToScreen(
              selectedField.x,
              selectedField.y,
              selectedField.width,
              selectedField.height,
            );
            // The anchor is the opposite corner
            const anchorX =
              handle === 'tl' || handle === 'bl' ? screenX + screenW : screenX;
            const anchorY =
              handle === 'tl' || handle === 'tr' ? screenY + screenH : screenY;
            setResizing(true);
            setResizeHandle(handle);
            setResizeAnchor({ x: anchorX, y: anchorY });
            return;
          }
        }
      }

      // Check if clicking on a field
      const clickedField = getFieldAtScreenPos(mouseX, mouseY);
      if (clickedField) {
        setSelectedFieldId(clickedField._id);
        const { screenX, screenY } = pdfToScreen(
          clickedField.x,
          clickedField.y,
          clickedField.width,
          clickedField.height,
        );
        setDragging(true);
        setDragOffset({ x: mouseX - screenX, y: mouseY - screenY });
        return;
      }

      // Clicked on empty area — deselect
      setSelectedFieldId(null);
    },
    [
      fields,
      selectedFieldId,
      currentPage,
      pdfToScreen,
      getFieldAtScreenPos,
      getResizeHandleAtPos,
    ],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging && !resizing) return;
      const rect = pageContainerRef.current?.getBoundingClientRect();
      if (!rect || selectedFieldId === null) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const selectedField = fields.find((f) => f._id === selectedFieldId);
      if (!selectedField) return;

      const canvasW = pageWidth * scale;
      const canvasH = pageHeight * scale;

      if (dragging) {
        const { screenW, screenH } = pdfToScreen(
          selectedField.x,
          selectedField.y,
          selectedField.width,
          selectedField.height,
        );
        const newScreenX = Math.max(
          0,
          Math.min(mouseX - dragOffset.x, canvasW - screenW),
        );
        const newScreenY = Math.max(
          0,
          Math.min(mouseY - dragOffset.y, canvasH - screenH),
        );
        const converted = screenToPdf(newScreenX, newScreenY, screenW, screenH);
        updateField(selectedFieldId, {
          x: converted.pdfX,
          y: converted.pdfY,
        });
      }

      if (resizing && resizeHandle) {
        const minW =
          selectedField.field_type === 'signature'
            ? MIN_SIGNATURE_SCREEN_W
            : MIN_DATE_SCREEN_W;
        const minH =
          selectedField.field_type === 'signature'
            ? MIN_SIGNATURE_SCREEN_H
            : MIN_DATE_SCREEN_H;

        // The moving corner follows the mouse, anchor stays fixed
        let left = Math.min(mouseX, resizeAnchor.x);
        let top = Math.min(mouseY, resizeAnchor.y);
        let right = Math.max(mouseX, resizeAnchor.x);
        let bottom = Math.max(mouseY, resizeAnchor.y);

        let w = right - left;
        let h = bottom - top;

        // Enforce minimums
        if (w < minW) {
          w = minW;
          if (mouseX < resizeAnchor.x) {
            left = resizeAnchor.x - minW;
          } else {
            right = resizeAnchor.x + minW;
            left = resizeAnchor.x;
          }
        }
        if (h < minH) {
          h = minH;
          if (mouseY < resizeAnchor.y) {
            top = resizeAnchor.y - minH;
          } else {
            bottom = resizeAnchor.y + minH;
            top = resizeAnchor.y;
          }
        }

        // Constrain to page bounds
        left = Math.max(0, left);
        top = Math.max(0, top);
        right = Math.min(canvasW, left + w);
        bottom = Math.min(canvasH, top + h);
        w = right - left;
        h = bottom - top;

        const converted = screenToPdf(left, top, w, h);
        updateField(selectedFieldId, {
          x: converted.pdfX,
          y: converted.pdfY,
          width: converted.pdfW,
          height: converted.pdfH,
        });
      }
    },
    [
      dragging,
      resizing,
      resizeHandle,
      resizeAnchor,
      selectedFieldId,
      fields,
      dragOffset,
      pageWidth,
      pageHeight,
      scale,
      pdfToScreen,
      screenToPdf,
      updateField,
    ],
  );

  const handleMouseUp = useCallback(() => {
    setDragging(false);
    setResizing(false);
    setResizeHandle(null);
  }, []);

  // Build summary of fields per page
  const fieldSummary = fields.reduce<
    Record<number, { signature: number; date: number }>
  >((acc, f) => {
    if (!acc[f.page]) acc[f.page] = { signature: 0, date: 0 };
    acc[f.page][f.field_type]++;
    return acc;
  }, {});

  const summaryParts: string[] = [];
  const sortedPages = Object.keys(fieldSummary)
    .map(Number)
    .sort((a, b) => a - b);
  for (const page of sortedPages) {
    const { signature, date } = fieldSummary[page];
    const parts: string[] = [];
    if (signature > 0) parts.push(`${signature} signature`);
    if (date > 0) parts.push(`${date} date`);
    summaryParts.push(`Page ${page}: ${parts.join(', ')}`);
  }

  return (
    <div ref={containerRef}>
      <Stack gap={8} style={{ marginBottom: '12px', alignItems: 'center' }}>
        <Button
          a11yLabel="Previous page"
          appearance="outline"
          label="Prev"
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        />
        <Body>
          Page {currentPage} of {numPages || '...'}
        </Body>
        <Button
          a11yLabel="Next page"
          appearance="outline"
          label="Next"
          disabled={currentPage >= numPages}
          onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
        />
        <div style={{ marginLeft: '16px' }}>
          <Button
            a11yLabel="Add signature field"
            appearance="outline"
            label="Add Signature"
            onClick={() => addField('signature')}
          />
        </div>
        <Button
          a11yLabel="Add date field"
          appearance="outline"
          label="Add Date"
          onClick={() => addField('date')}
        />
      </Stack>

      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          overflow: 'auto',
          maxHeight: '600px',
          background: '#f5f5f5',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {pdfLoading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Body>Loading PDF...</Body>
          </div>
        ) : pdfError ? (
          <div style={{ padding: '20px', color: 'red' }}>
            <Body>{pdfError}</Body>
          </div>
        ) : (
          <div
            ref={pageContainerRef}
            style={{
              position: 'relative',
              cursor: dragging
                ? 'grabbing'
                : resizing
                  ? 'nwse-resize'
                  : 'default',
              userSelect: 'none',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <canvas ref={canvasRef} style={{ display: 'block' }} />

            {fieldsOnCurrentPage.map((field) => {
              const isSelected = field._id === selectedFieldId;
              const isSignature = field.field_type === 'signature';
              const color = isSignature ? '#e53e3e' : '#3182ce';
              const label = isSignature ? 'Signature' : 'Date';
              const { screenX, screenY, screenW, screenH } = pdfToScreen(
                field.x,
                field.y,
                field.width,
                field.height,
              );

              return (
                <div
                  key={field._id}
                  style={{
                    position: 'absolute',
                    left: `${screenX}px`,
                    top: `${screenY}px`,
                    width: `${screenW}px`,
                    height: `${screenH}px`,
                    border: `2px ${isSelected ? 'solid' : 'dashed'} ${color}`,
                    borderRadius: '4px',
                    backgroundColor: isSignature
                      ? 'rgba(229, 62, 62, 0.1)'
                      : 'rgba(49, 130, 206, 0.1)',
                    cursor: dragging && isSelected ? 'grabbing' : 'grab',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'auto',
                    zIndex: isSelected ? 20 : 10,
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      color,
                      fontWeight: 600,
                      userSelect: 'none',
                    }}
                  >
                    {label}
                  </span>

                  {/* Delete button */}
                  {isSelected && (
                    <div
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        deleteField(field._id);
                      }}
                      style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '-10px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: color,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        zIndex: 30,
                        lineHeight: 1,
                      }}
                    >
                      {'\u00d7'}
                    </div>
                  )}

                  {/* Resize handles */}
                  {isSelected &&
                    (['tl', 'tr', 'bl', 'br'] as ResizeHandle[]).map(
                      (handle) => {
                        const isLeft = handle === 'tl' || handle === 'bl';
                        const isTop = handle === 'tl' || handle === 'tr';
                        return (
                          <div
                            key={handle}
                            style={{
                              position: 'absolute',
                              width: `${HANDLE_SIZE}px`,
                              height: `${HANDLE_SIZE}px`,
                              backgroundColor: color,
                              border: '1px solid #fff',
                              left: isLeft
                                ? `${-HANDLE_SIZE / 2}px`
                                : `${screenW - HANDLE_SIZE / 2}px`,
                              top: isTop
                                ? `${-HANDLE_SIZE / 2}px`
                                : `${screenH - HANDLE_SIZE / 2}px`,
                              cursor:
                                handle === 'tl' || handle === 'br'
                                  ? 'nwse-resize'
                                  : 'nesw-resize',
                              zIndex: 30,
                            }}
                          />
                        );
                      },
                    )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginTop: '8px' }}>
        <Small>
          {fields.length === 0
            ? 'Use the buttons above to add signature and date fields.'
            : summaryParts.join(' | ')}
        </Small>
      </div>
    </div>
  );
};

export default PdfSignaturePlacer;
